import React, { useState } from "react";
import { Form, Input, Button, Spin, Row, Col, TimePicker, Modal } from "antd";
import { ReactSVG } from "react-svg";
import { Link } from "react-router-dom";
import { isEmpty } from "lodash";
import { useSelector } from "react-redux";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";

import { TIME_FORMAT_FRONTEND } from "../../../constants";
import AddressInput from "../../Inputs/AddressInput";
import { getTranslation, setCountryCookie } from "../../../utils/helper";
import DefaultContactLocation from "./DefaultContactLocation";
import dayjs from "dayjs";

const PickupForm = ({ form, isFetching, handleSubmit, handleCancel, countryCodeInit }) => {
  const user = useSelector(state => state.auth.user);
  const setting = user.company;

  const [countryPhone, setCountryPhone] = useState();
  const [state, setState] = useState({
    visibleModal: false,
    contactType: null,
    contact: null,
  });

  const handleOnFinish = values => {
    const params = { ...values };
    params.pickupPhone =
      !isEmpty(values.pickupPhone) && countryPhone?.dialCode !== values.pickupPhone
        ? `${values.pickupPhone.includes("+") ? "" : "+"}${values.pickupPhone}`
        : null;
    handleSubmit(params);
  };

  const handleOnChangePickupFrom = value => {
    form.setFieldsValue({ timeStart: value });
    if (isEmpty(value) || isEmpty(form.getFieldValue("timeEnd"))) {
      resetErrorPickupTime();
      return;
    }
    if (form.getFieldValue("timeEnd") < value) {
      form.setFields([
        {
          name: "timeStart",
          errors: ["Shift End must be less than Shift Start"],
        },
      ]);
    }
  };

  const handleOnChangePickupTo = value => {
    form.setFieldsValue({ timeEnd: value });
    if (isEmpty(value) || isEmpty(form.getFieldValue("timeStart"))) {
      resetErrorPickupTime();
      return;
    }
    if (form.getFieldValue("timeStart") > value) {
      form.setFields([
        {
          name: "timeEnd",
          errors: ["Pickup To must be greater than Pickup From"],
        },
      ]);
    }
  };

  const resetErrorPickupTime = () => {
    form.setFields([
      {
        name: "timeStart",
        errors: [],
      },
      {
        name: "timeEnd",
        errors: [],
      },
    ]);
  };

  const handleUseDefaultLocation = () => {
    setState({
      ...state,
      visibleModal: true,
      contactType: "PICKUP",
      contact: null,
    });
  };

  const handleSubmitSelectContact = () => {
    if (state.contact) {
      form.setFieldsValue({
        pickupName: state.contact.name,
        pickupPhone: state.contact.phone,
        timeStart: state.contact.startTime ? dayjs(state.contact.startTime, TIME_FORMAT_FRONTEND) : null,
        timeEnd: state.contact.endTime ? dayjs(state.contact.endTime, TIME_FORMAT_FRONTEND) : null,
        pickupDuration: state.contact.duration,
        pickupAddress: {
          address: state.contact.address,
          latLng: {
            lat: state.contact.lat,
            lng: state.contact.lng,
          },
        },
      });
    }
    handleCancelSelectContact();
  };

  const handleCancelSelectContact = () => {
    setState({ ...state, visibleModal: false, contact: null });
  };

  return (
    <Spin spinning={isFetching}>
      <div className="PickupForm">
        <Form form={form} scrollToFirstError layout="vertical" hideRequiredMark colon={false} onFinish={handleOnFinish}>
          <div className="FormHeader">
            <div className="left">{getTranslation(setting, "Orders", "Edit Pickup")}</div>
            <div className="right">
              <Link to="#" onClick={handleUseDefaultLocation}>
                {getTranslation(setting, "Orders", "Add from Contact List")}
                <ReactSVG className="icon" src="/icons/default-location.svg" />
              </Link>
            </div>
          </div>
          <div className="FormContent">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label={getTranslation(setting, "Orders", "Pickup Name")} name="pickupName">
                  <Input size="large" placeholder={getTranslation(setting, "Orders", "Pickup Name")} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={getTranslation(setting, "Orders", "Pickup Phone Number")} name="pickupPhone">
                  <PhoneInput
                    country={countryCodeInit}
                    enableSearch
                    disableSearchIcon
                    onChange={(value, country) => {
                      setCountryPhone(country);
                      setCountryCookie(value, country, user);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={getTranslation(setting, "Orders", "Pickup Email")}
                  name="pickupEmail"
                  rules={[{ type: "email", message: "Invalid Email" }]}>
                  <Input size="large" placeholder={getTranslation(setting, "Orders", "Pickup Email")} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label={getTranslation(setting, "Orders", "Pickup From")} name="timeStart">
                  <TimePicker
                    size="large"
                    placeholder={getTranslation(setting, "Orders", "Pickup From")}
                    format={TIME_FORMAT_FRONTEND}
                    minuteStep={30}
                    style={{ width: "100%" }}
                    onChange={handleOnChangePickupFrom}
                    onCalendarChange={handleOnChangePickupFrom}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label={getTranslation(setting, "Orders", "Pickup To")} name="timeEnd">
                  <TimePicker
                    size="large"
                    placeholder={getTranslation(setting, "Orders", "Pickup To")}
                    format={TIME_FORMAT_FRONTEND}
                    minuteStep={30}
                    style={{ width: "100%" }}
                    onChange={handleOnChangePickupTo}
                    onCalendarChange={handleOnChangePickupTo}
                  />
                </Form.Item>
              </Col>
              <Col span={18}>
                <Form.Item
                  label={getTranslation(setting, "Orders", "Pickup Address")}
                  name="pickupAddress"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (value?.address && value?.latLng?.lat && value?.latLng?.lng) {
                          return Promise.resolve();
                        }

                        if (isEmpty(value?.address)) {
                          return Promise.reject(new Error("Required"));
                        }

                        return Promise.reject(new Error("Invalid"));
                      },
                    },
                  ]}>
                  <AddressInput
                    size="large"
                    placeholder={getTranslation(setting, "Orders", "Pickup Address")}
                    onSelect={value => form.setFieldsValue({ pickupAddress: value })}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label={getTranslation(setting, "Orders", "Pickup Duration (minutes)")} name="pickupDuration">
                  <Input size="large" placeholder={getTranslation(setting, "Orders", "Pickup Duration (minutes)")} />
                </Form.Item>
              </Col>
            </Row>
          </div>
          <div className="FormFooter">
            <div className="Left"></div>
            <div className="Right">
              <Button key="cancel" onClick={handleCancel}>
                {getTranslation(setting, "Common", "Cancel")}
              </Button>
              <Button htmlType="submit" type="primary">
                {getTranslation(setting, "Common", "Update")}
                <ReactSVG className="icon" src="/icons/submit.svg" />
              </Button>
            </div>
          </div>
        </Form>
      </div>
      <Modal open={state.visibleModal} onCancel={handleCancelSelectContact} footer={[]} width={792}>
        <DefaultContactLocation
          state={state}
          setState={setState}
          handleCancel={handleCancelSelectContact}
          handleSubmit={handleSubmitSelectContact}
        />
      </Modal>
    </Spin>
  );
};

export default PickupForm;
